import React, { createContext, useContext, useState, useEffect } from 'react';

const ConfigContext = createContext();

export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
};

export const ConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(null);
  const [isConfigured, setIsConfigured] = useState(false);

  useEffect(() => {
    // Load configuration from localStorage on app start
    const savedConfig = localStorage.getItem('ai_recruitment_config');
    if (savedConfig) {
      try {
        const parsedConfig = JSON.parse(savedConfig);
        setConfig(parsedConfig);
        setIsConfigured(true);
      } catch (error) {
        console.error('Error parsing saved configuration:', error);
        localStorage.removeItem('ai_recruitment_config');
      }
    }
  }, []);

  const saveConfig = (newConfig) => {
    try {
      localStorage.setItem('ai_recruitment_config', JSON.stringify(newConfig));
      setConfig(newConfig);
      setIsConfigured(true);
    } catch (error) {
      console.error('Error saving configuration:', error);
      throw new Error('Failed to save configuration');
    }
  };

  const clearConfig = () => {
    localStorage.removeItem('ai_recruitment_config');
    setConfig(null);
    setIsConfigured(false);
  };

  const validateConfig = (configData) => {
    const required = ['apiGatewayUrl', 'cognitoUserPoolId', 'cognitoClientId', 'region'];
    const missing = required.filter(field => !configData[field] || configData[field].trim() === '');
    
    if (missing.length > 0) {
      throw new Error(`Missing required fields: ${missing.join(', ')}`);
    }

    // Validate URL format
    try {
      new URL(configData.apiGatewayUrl);
    } catch {
      throw new Error('Invalid API Gateway URL format');
    }

    // Validate region format (basic check)
    if (!/^[a-z0-9-]+$/.test(configData.region)) {
      throw new Error('Invalid AWS region format');
    }

    return true;
  };

  const value = {
    config,
    isConfigured,
    saveConfig,
    clearConfig,
    validateConfig,
  };

  return (
    <ConfigContext.Provider value={value}>
      {children}
    </ConfigContext.Provider>
  );
};
